import React, { useMemo } from "react";
import { CloseIcon } from "../general/Icons";
import { useTranslation } from "react-i18next";
import { useCard, useSurcharge, useTicket } from "../../store/Store";
import {
  getBalanceDue,
} from "../general/AppHelpers";
import HelpIcon from "@mui/icons-material/Help";
import Tooltip from "@mui/material/Tooltip";
import Currency from "../general/Currency";
import { getLabelTextForSurchargeItem, isCloud9 } from "./api";


const SurchargeConfirmationModal = ({ onConfirm, onClose }) => {
  const { t } = useTranslation();
  const { data: card } = useCard();
  const { data: surcharge } = useSurcharge();
  const { data: ticketData } = useTicket();
  const { resource } = ticketData || {};
  const balance_due = getBalanceDue(ticketData);

  const surchargeTotal = useMemo(() => {
    const surchargeItems = resource.adjustments.filter(
      (adj) => adj.adjustable_type === "Pos::SurchargeItem" && !("id" in adj)
    );
    return surchargeItems.reduce((total, adj) => total + adj.amount, 0);
  }, [resource?.adjustments]);

  const totalAmount = useMemo(() => {
    return getBalanceDue(ticketData) + surchargeTotal;
  }, [ticketData, surchargeTotal]);

  const surchargeLabelText = useMemo(() => {
    const configuration = surcharge?.surcharge_configuration_item;
    return getLabelTextForSurchargeItem({
      ...configuration,
      currency: ticketData?.resource?.company?.currency_uuid,
      locale: t?.language,
    });

  }, [surcharge?.surcharge_configuration_item, t?.language, ticketData?.resource?.company?.currency_uuid]);

  const tooltipKey = isCloud9(ticketData)
    ? "surcharge.tooltipCloud9"
    : "surcharge.tooltip";

  // =============Modal Components=================
  const ModalHeader = () => {
    return (
      <div className="bg-gray-50 p-4 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-black uppercase">
          {t("surcharge.modal.title")}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <CloseIcon />
        </button>
      </div>
    );
  };

  const ModalFooter = () => {
    return (
      <div className="p-4 mt-3 flex justify-between bg-gray-50">
        <p
          onClick={onClose}
          className="w-full inline-flex justify-center py-2 text-base font-medium text-primary sm:w-auto sm:text-sm cursor-pointer"
        >
          {t("surcharge.modal.different_payment_method")}
        </p>

        <button
          type="button"
          onClick={onConfirm}
          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm bg-primary focus:ring-primary`}
        >
          {t("surcharge.modal.pay_button")} <Currency value={totalAmount} />
        </button>
      </div>
    );
  };

  const PaymentSummary = () => {
    return (
      <div className="mt-3 text-base text-gray-700">
        <p className="text-md leading-6 font-medium text-black mb-2">
          {t("surcharge.modal.payment_summary")}
        </p>

        <div className="flex justify-between mb-2">
          <span>{t("surcharge.modal.subtotal")}</span>
          <span>
            <Currency value={balance_due} />
          </span>
        </div>

        <div className="flex justify-between mb-2">
          <span className="inline-flex items-center">
            {surchargeLabelText}
            <span className="ml-2">
              <Tooltip
                title={t(tooltipKey)}
                arrow
                placement="top"
                className="flex-1"
              >
                <HelpIcon className="text-primary text-sm" />
              </Tooltip>
            </span>
          </span>
          <span>
            <Currency value={surchargeTotal} />
          </span>
        </div>

        <div className="flex justify-between text-md font-bold leading-5">
          <span>{t("surcharge.modal.total")}</span>
          <span>
            <Currency value={totalAmount} />
          </span>
        </div>
      </div>
    );
  };

  const ConfirmationNote = () => {
    return (
      <div className="mt-3">
        <p className="text-base text-gray-500">
          <i>{t("surcharge.modal.authorization_note")}</i>
        </p>
        <p className="mt-4 text-base text-gray-800">
          {t("surcharge.modal.confirmation_question")}
        </p>
      </div>
    );
  };

  const ModalContent = () => {
    return (
      <div className="p-4 sm:flex sm:items-center">
        <div className="mt-3 text-center sm:mt-0 sm:text-left flex-1 ">
          <div className="mt-2">
            <p className="text-base text-gray-600 [&_a]:text-blue-600 [&_a]:underline">
              {t("surcharge.modal.payment_method_description", {
                brand: card?.brand?.toUpperCase(),
                last4: card?.last4,
              })}
            </p>
          </div>

          <Divider className="-mx-4 my-4" />

          <PaymentSummary />

          <Divider className="-mx-4 my-2" />

          <ConfirmationNote />
        </div>
      </div>
    );
  };

  const Divider = ({ className }) => {
    return <div className={`border-b border-gray-300 ${className}`} />;
  };

  // =================================================

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-999"
      data-testid="modal-container"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>
      <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full m-4">
        <ModalHeader />
        <Divider />
        <ModalContent />
        <ModalFooter />
      </div>
    </div>
  );
};

export default SurchargeConfirmationModal;
